import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, useTheme, IconButton, Skeleton } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

import { useSubmissionsByDealer } from 'services/Data/SubmissionServices';
import { useCurrentQuarter } from 'services/Data/QuarterServices';
import { isSubmissionCompletedBucket } from 'services/Utilities/submissionUtils';
import CreateSubmissionDialog from '../components/CreateSubmissionDialog';
import LinearProgressLabel from './components/LinearProgressLabel';
import { getHomePageTourSteps } from '../tourSteps/homePageTourSteps';
import GlobalTour from '../tourSteps/GlobalTour';
import CertificationHeroCarousel from './components/CertificationHeroCarousel';

const HomePage = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const account = useSelector(state => state.AccountReducer);
  const dealerId = account?.profile?.dealer_Id || null;
  const { data: submissions, isLoading: isLoadingSubmissions } = useSubmissionsByDealer(dealerId);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const currentLanguage = i18n.language;
  const { data: currentQuarter } = useCurrentQuarter();
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourSteps, setTourSteps] = useState([]);

  useEffect(() => {
    setTourSteps(getHomePageTourSteps(t));
  }, [t]);

  const nextTourStep = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      setShowTour(false);
      setTourStep(0);
    }
  };

  const closeTour = () => {
    setShowTour(false);
    setTourStep(0);
  };

  function startTour() {
    const cleaned = tourSteps.filter(step => document.querySelector(step.target));

    setTourSteps(cleaned);
    setTourStep(0);
    setShowTour(true);
  }

  // Calculate stats from submissions
  const stats = useMemo(() => {
    if (!submissions || !Array.isArray(submissions)) {
      return { active: 0, completed: 0 };
    }

    return submissions.reduce(
      (acc, submission) => {
        if (isSubmissionCompletedBucket(submission)) {
          acc.completed += 1;
        } else {
          acc.active += 1;
        }
        return acc;
      },
      { active: 0, completed: 0 },
    );
  }, [submissions]);

  const certifiedUntil = useMemo(() => {
    const now = new Date();

    return (
      submissions
        ?.filter(s => {
          const date = s.nextSubmissionQuarter?.startDate;
          return date && new Date(date) > now;
        })
        ?.sort((a, b) => new Date(b.nextSubmissionQuarter.startDate) - new Date(a.nextSubmissionQuarter.startDate))[0] ||
      null
    );
  }, [submissions]);

  const formattedCertifiedUntil = certifiedUntil?.nextSubmissionQuarter?.startDate
    ? new Date(certifiedUntil.nextSubmissionQuarter.startDate).toLocaleDateString(
        currentLanguage === 'fr' ? 'fr-CA' : 'en-US',
        {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        },
      )
    : null;

  const carouselSlides = [
    {
      image: '/images/certification/brp2023-07-mta-my24-int-photo-tropical-final.avif',
      category: t('carousel.certification'),
      title: t('carousel.certificationTitle'),
      subtitle: t('carousel.certificationSubtitle'),
    },
    {
      image: '/images/certification/vision.avif',
      category: t('carousel.excellence'),
      title: t('carousel.excellenceTitle'),
      subtitle: t('carousel.excellenceSubtitle'),
    },
    {
      image: '/images/certification/lynx-my22-49r-900ace-snowy-forest-northern-lights-62a1152.avif',
      category: t('carousel.progress'),
      title: t('carousel.progressTitle'),
      subtitle: t('carousel.progressSubtitle'),
    },
    {
      image: '/images/certification/csr-ev-capacity.avif',
      category: t('carousel.innovation'),
      title: t('carousel.innovationTitle'),
      subtitle: t('carousel.innovationSubtitle'),
    },
  ];

  const dashboardCards = [
    {
      key: 'create',
      type: 'action',
      title: t('submissions.createNew'),
      description: t('submissions.selectCriteriaToBegin'),
      icon: <AddIcon sx={{ fontSize: 44 }} />,
      color: theme.palette.mode === 'dark' ? '#FFD200' : '#B8941E',
      action: () => setCreateDialogOpen(true),
      tourAttr: 'create-submission',
    },
    {
      key: 'active',
      type: 'stat',
      title: t('home.activeCertifications'),
      description: t('home.activeCertificationsDesc'),
      icon: <CheckCircleIcon sx={{ fontSize: 48 }} />,
      path: '/dealer/certification/submissions',
      color: '#FFD200',
      count: isLoadingSubmissions ? null : stats.active,
      tourAttr: 'active-certifications',
    },
    {
      key: 'completed',
      type: 'stat',
      title: t('home.completed'),
      description: t('home.completedDesc'),
      icon: <HistoryIcon sx={{ fontSize: 48 }} />,
      path: '/dealer/certification/submissions#completed-submissions-list',
      color: '#00bfdf',
      count: isLoadingSubmissions ? null : stats.completed,
      tourAttr: 'completed-certifications',
    },
  ];

  return (
    <>
      <Box>
        <IconButton
          onClick={() => startTour()}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: '#FFD200',
            color: '#000',
            zIndex: 1000,
            '&:hover': {
              bgcolor: '#B8941E',
            },
          }}>
          <HelpIcon />
        </IconButton>

        {/* Hero Carousel - Fullscreen */}
        <CertificationHeroCarousel slides={carouselSlides} />

        <Box
          sx={{
            maxWidth: { xs: '100%', md: 1200, lg: 1400, xl: 1600 },
            mx: 'auto',
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
          }}>
          {/* Dashboard Content - Centered with Max Width */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              mt: 10,
              mb: -10,
            }}>
            {!formattedCertifiedUntil && currentQuarter ? (
              <LinearProgressLabel quarter={currentQuarter} />
            ) : (
              <Typography
                data-tour="certified-until"
                variant="h1"
                sx={{
                  textAlign: 'center',
                  color: theme.palette.text.primary,
                  fontWeight: 900,
                  fontSize: { xs: 42, md: 60 },
                }}>
                {t('home.certifiedUntil', {
                  certifiedUntil: formattedCertifiedUntil,
                })}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              maxWidth: { xs: '100%', sm: 720, md: 960, lg: 1280, xl: 1920 },
              mx: 'auto',
              pt: { xs: 10, md: 15 },
              pb: { xs: 3, md: 6 },
              px: { xs: 2, sm: 3, md: 4 },
            }}>
            <br />
            {/* Stats Cards */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                borderRadius: 4,
                p: { xs: 3, md: 5 },
                border:
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : `1px solid ${theme.palette.borderColor.main}`,
              }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: { xs: 4, md: 6 },
                }}>
                {dashboardCards.map((card, index) => {
                  const isActionCard = card.type === 'action';
                  const showStatSkeleton = card.type === 'stat' && isLoadingSubmissions;

                  return (
                    <Box
                      key={card.key}
                      data-tour={card.tourAttr}
                      onClick={() => {
                        if (isActionCard) {
                          card.action();
                        } else if (card.path) {
                          history.push(card.path);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          if (isActionCard) {
                            card.action();
                          } else if (card.path) {
                            history.push(card.path);
                          }
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        textAlign: 'center',
                        py: 3,
                        px: 2,
                        borderRight:
                          index < dashboardCards.length - 1
                            ? {
                                xs: 'none',
                                sm:
                                  theme.palette.mode === 'dark'
                                    ? '1px solid rgba(255, 255, 255, 0.08)'
                                    : `1px solid ${theme.palette.borderColor.main}`,
                              }
                            : 'none',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          '& .stat-number': {
                            color: '#FFD200',
                            transform: 'scale(1.05)',
                          },
                          '& .action-icon': {
                            transform: 'scale(1.08)',
                            boxShadow: `0 8px 16px ${card.color}40`,
                          },
                        },
                        outline: 'none',
                      }}>
                      {isActionCard ? (
                        <Box
                          className="action-icon"
                          sx={{
                            width: 88,
                            height: 88,
                            borderRadius: '50%',
                            bgcolor: `${card.color}1A`,
                            color: card.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            boxShadow: `0 6px 14px ${card.color}26`,
                          }}>
                          {card.icon}
                        </Box>
                      ) : (
                        <>
                          {showStatSkeleton ? (
                            <Skeleton
                              variant="text"
                              sx={{
                                mb: 1,
                                mx: 'auto',
                                transform: 'none',
                              }}
                              width={72}
                              height={72}
                            />
                          ) : (
                            <Typography
                              className="stat-number"
                              variant="h1"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 900,
                                fontSize: { xs: 42, md: 60 },
                                mb: 1,
                                transition: 'all 0.3s ease',
                              }}>
                              {card.count}
                            </Typography>
                          )}
                        </>
                      )}

                      <Typography
                        variant="h1"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: { xs: 16, md: 18 },
                        }}>
                        {card.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: { xs: 13, md: 14 },
                          lineHeight: 1.6,
                          maxWidth: 240,
                        }}>
                        {card.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <GlobalTour show={showTour} steps={tourSteps} stepIndex={tourStep} onClose={closeTour} onNext={nextTourStep} t={t} />

      <CreateSubmissionDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />
    </>
  );
};

export default HomePage;
